import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Inbox } from "lucide-react";
import api from "@/lib/api";
import { useNotifications } from "@/context/NotificationContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const relTime = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "baru saja";
  if (s < 3600) return `${Math.floor(s / 60)} mnt lalu`;
  if (s < 86400) return `${Math.floor(s / 3600)} jam lalu`;
  return `${Math.floor(s / 86400)} hari lalu`;
};

export default function NotificationBell() {
  const { enabled, unreadCount, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/admin/notifications", { params: { limit: 8 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, unreadCount]);

  if (!enabled) return null;

  const onClickItem = async (n) => {
    setOpen(false);
    if (!n.is_read) markRead(n.id).catch(() => {});
    navigate(n.action_url || "/admin/notifications");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" aria-label="Notifikasi"
          className="relative w-10 h-10 rounded-xl grid place-items-center bg-[color:var(--brand-surface)] border border-[color:var(--brand-border)] hover:brand-soft-bg transition-colors">
          <Bell size={18} className="text-[color:var(--brand-muted)]" />
          {unreadCount > 0 && (
            <span data-testid="notification-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full brand-bg-primary text-white text-[10px] font-bold grid place-items-center">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0 rounded-2xl overflow-hidden" data-testid="notification-dropdown">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-bold text-sm">Notifikasi</span>
          {unreadCount > 0 && (
            <button onClick={() => markAllRead()} data-testid="notification-mark-all-read"
              className="text-xs font-bold brand-text-primary flex items-center gap-1 hover:opacity-70">
              <CheckCheck size={13} /> Tandai semua dibaca
            </button>
          )}
        </div>
        <div className="max-h-[360px] overflow-auto">
          {loading ? (
            <div className="p-4 space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-12 rounded-xl bg-gray-100 animate-pulse" />)}</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400" data-testid="notification-empty">
              <Inbox size={26} className="mx-auto mb-2 opacity-40" />Belum ada notifikasi.
            </div>
          ) : items.map((n) => (
            <button key={n.id} onClick={() => onClickItem(n)} data-testid={`notification-item-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-gray-50 transition-colors flex gap-2.5 ${!n.is_read ? "bg-[color:var(--brand-background)]" : ""}`}>
              {!n.is_read && <span className="mt-1.5 w-2 h-2 rounded-full brand-bg-primary shrink-0" data-testid="notification-unread-dot" />}
              <span className={`flex-1 min-w-0 ${n.is_read ? "pl-4" : ""}`}>
                <span className="block text-sm font-bold truncate">{n.title}</span>
                <span className="block text-xs text-gray-500 line-clamp-2">{n.message}</span>
                <span className="block text-[10px] text-gray-400 mt-0.5">{relTime(n.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
        <Link to="/admin/notifications" onClick={() => setOpen(false)} data-testid="notification-view-all"
          className="block text-center text-xs font-bold brand-text-primary py-2.5 border-t hover:bg-gray-50">
          Lihat semua
        </Link>
      </PopoverContent>
    </Popover>
  );
}
